import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Form, Input, Button, Card, Typography, Alert, Space, Result, Spin } from 'antd'
import { LockOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import api from '../services/api'

const { Title, Text, Paragraph } = Typography

const ResetPassword = () => {
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [tokenValid, setTokenValid] = useState(false)
  const [email, setEmail] = useState('')
  const [passwordRequirements, setPasswordRequirements] = useState(null)
  const navigate = useNavigate()
  const token = searchParams.get('token')

  useEffect(() => {
    verifyToken()
    fetchPasswordRequirements()
  }, [])

  const verifyToken = async () => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.')
      setVerifying(false)
      return
    }

    try {
      const response = await api.post('/auth/verify-reset-token', {
        token
      })

      if (response.valid) {
        setTokenValid(true)
        setEmail(response.email)
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired reset link.')
    } finally {
      setVerifying(false)
    }
  }

  const fetchPasswordRequirements = async () => {
    try {
      const response = await api.get('/auth/password-requirements')
      setPasswordRequirements(response)
    } catch (err) {
      console.error('Failed to fetch password requirements:', err)
    }
  }

  const onFinish = async (values) => {
    setLoading(true)
    setError('')

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: values.password
      })

      setSuccess(true)
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (verifying) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4"
        style={{
          backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Card
          className="w-full max-w-md shadow-2xl"
          bodyStyle={{ padding: '3rem' }}
        >
          <div className="text-center">
            <Spin size="large" />
            <Title level={4} className="mt-4">Verifying reset link...</Title>
          </div>
        </Card>
      </div>
    )
  }

  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4"
        style={{
          backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Card
          className="w-full max-w-md shadow-2xl"
          bodyStyle={{ padding: window.innerWidth < 640 ? '1.5rem' : '2rem' }}
        >
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="Password Reset Successful"
            subTitle="Your password has been successfully reset. You can now login with your new password."
            extra={[
              <Button
                type="primary"
                key="login"
                onClick={() => navigate('/login')}
                size="large"
                className="gradient-primary"
              >
                Go to Login
              </Button>
            ]}
          />
        </Card>
      </div>
    )
  }

  if (!tokenValid) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4"
        style={{
          backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Card
          className="w-full max-w-md shadow-2xl"
          bodyStyle={{ padding: window.innerWidth < 640 ? '1.5rem' : '2rem' }}
        >
          <Result
            status="error"
            icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
            title="Invalid Reset Link"
            subTitle={error || 'This password reset link is invalid or has expired.'}
            extra={[
              <Link to="/forgot-password" key="forgot">
                <Button type="primary" size="large" className="gradient-primary">
                  Request New Reset Link
                </Button>
              </Link>,
              <Link to="/login" key="login">
                <Button size="large">
                  Back to Login
                </Button>
              </Link>
            ]}
          />
        </Card>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4"
      style={{
        backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Card
        className="w-full max-w-md shadow-2xl"
        bodyStyle={{ padding: window.innerWidth < 640 ? '1.5rem' : '2rem' }}
      >
        <Space direction="vertical" size="large" className="w-full">
          {/* Logo and Title */}
          <div className="text-center">
            <div className="flex flex-col items-center mb-6">
              <img
                src="/Icon.png"
                alt="ACIS"
                className="w-20 h-20 mb-3"
              />
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-gray-900 mb-1">ACIS</span>
                <span className="text-sm text-gray-600">Alpha Centauri Investment Strategies Inc.</span>
              </div>
            </div>
            <Title level={2} className="mb-0">
              Set New Password
            </Title>
            <Text type="secondary">
              for {email}
            </Text>
          </div>

          {/* Password Requirements */}
          {passwordRequirements && (
            <Alert
              message="Password Requirements"
              description={
                <ul className="list-disc pl-5 mb-0 text-sm">
                  <li>At least {passwordRequirements.min_length} characters long</li>
                  {passwordRequirements.require_uppercase && <li>At least one uppercase letter</li>}
                  {passwordRequirements.require_lowercase && <li>At least one lowercase letter</li>}
                  {passwordRequirements.require_numbers && <li>At least one number</li>}
                  {passwordRequirements.require_special && <li>At least one special character</li>}
                </ul>
              }
              type="info"
              showIcon
            />
          )}

          {/* Error Alert */}
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
            />
          )}

          {/* Reset Password Form */}
          <Form
            name="reset-password"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please enter your new password' },
                { min: passwordRequirements?.min_length || 8, message: `Password must be at least ${passwordRequirements?.min_length || 8} characters` },
              ]}
              hasFeedback
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="New Password"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirm"
              dependencies={['password']}
              hasFeedback
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error('Passwords do not match'))
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm New Password"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                className="gradient-primary"
              >
                Reset Password
              </Button>
            </Form.Item>
          </Form>

          {/* Footer */}
          <div className="text-center text-gray-500 text-sm mt-4">
            <Text type="secondary">
              ACIS AI Platform - Admin Access Only
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  )
}

export default ResetPassword
